import { Link } from "@bond-london/gatsby-plugin-cms-i18next";
import {
  GatsbyAnimation,
  IGatsbyExtractedAnimation,
} from "@bond-london/gatsby-transformer-extracted-lottie";
import { IGatsbySvg } from "@bond-london/gatsby-transformer-extracted-svg";
import { IRichTextInformation } from "@bond-london/graphcms-rich-text";
import classNames from "classnames";
import { GatsbyImage, IGatsbyImageData } from "gatsby-plugin-image";
import React from "react";
import { Trans } from "react-i18next";
import { BackgroundGradient } from "../elements/BackgroundGradient";
import { DashedText } from "../elements/DashedText";
import { RTF } from "../elements/RTF";
import { Separator } from "../elements/Separator";
import { useEntryAnimation } from "../hooks/Visibility";
import {
  MainWidthClassName,
  P1RTFOverrides,
  SectionPaddingClassName,
} from "../styles";

export const ContentBlock: React.FC<{
  heading?: string | null;
  title?: string | null;
  image?: IGatsbyImageData | null;
  animation?: IGatsbyExtractedAnimation | null;
  svg?: IGatsbySvg | null;
  alt?: string | null;
  message?: IRichTextInformation | null;
  link?: string | null;
  linkTitle?: string | null;
  showLinkAsButton?: boolean;
  className?: string;
  gradientType?: Queries.GraphCMS_GradientType | null;
  gradientColour?: Queries.GraphCMS_Colour | null;
}> = ({
  heading,
  title,
  image,
  animation,
  svg,
  alt,
  message,
  link,
  linkTitle,
  showLinkAsButton,
  className = "",
  gradientColour,
  gradientType,
}) => {
  const [sectionRef, animationMode] = useEntryAnimation(0.2);
  const hasImage = image ?? svg ?? animation;

  return (
    <section
      ref={sectionRef}
      className={className}
      data-component="Content Block"
    >
      <BackgroundGradient
        gradientColour={gradientColour}
        gradientType={gradientType}
      />
      <div
        className={classNames(
          SectionPaddingClassName,
          "page-grid max-width relative auto-rows-max"
        )}
      >
        {title && (
          <h3
            className={classNames(
              "h3 text-hyacinth",
              MainWidthClassName,
              animationMode,
              "animate-enter-from-"
            )}
          >
            {title}
          </h3>
        )}
        {heading && (
          <h2
            className={classNames(
              MainWidthClassName,
              animationMode,
              "h1 animate-enter-from-left"
            )}
          >
            {heading}
          </h2>
        )}
        {message && <Separator className={classNames(MainWidthClassName)} />}
        {message && (
          <RTF
            className={classNames(
              MainWidthClassName,
              animationMode,
              "animate-enter-from-right"
            )}
            classNameOverrides={P1RTFOverrides}
            content={message}
          />
        )}
        {hasImage && (
          <div className={classNames(MainWidthClassName, "relative")}>
            {image && !animation && (
              <GatsbyImage
                className="absolute h-full w-full"
                image={image}
                alt={alt ?? "Sub Section Hero Image"}
                objectFit="contain"
                objectPosition="center"
                loading="lazy"
              />
            )}
            {svg && !animation && (
              <img
                src={svg.encoded ?? svg.encodedUrl}
                className="h-full w-full"
                alt={alt ?? "Sub Section Hero Image"}
              />
            )}
            {animation && <GatsbyAnimation animation={animation} />}
          </div>
        )}
        {link && (
          <>
            {!message && (
              <Separator className={classNames(MainWidthClassName)} />
            )}

            <Link
              to={link}
              aria-label={linkTitle ? linkTitle : "Read more"}
              className={classNames(
                showLinkAsButton ? "servicesButton px-2" : "h3 flex py-3",
                MainWidthClassName
              )}
            >
              <DashedText colorClassName="text-hyacinth flex">
                {linkTitle ? linkTitle : <Trans>Read more</Trans>}
              </DashedText>
            </Link>
          </>
        )}
      </div>
    </section>
  );
};
